/**
 * Multi-Platform Budget Tool (MPBT) Constants
 *
 * Configuration that drives the "Bulk Multi-Platform Budget Group Daily Create" tool.
 * This sits on top of the existing per-platform Budget Group Daily (BGD) forms and
 * lets a user create budget groups across multiple Platform + Budget Type
 * combinations (e.g. "Amazon - Item", "Google - Search", "Meta - Facebook Instagram")
 * for an account in one action.
 *
 * Design:
 * - MPBT_BUDGET_COMBOS: the fixed list of selectable Platform + Budget Type line
 *   items. Each is independently checkable and gets its own daily budget - this is
 *   NOT a "pick a platform, then pick one normalized type" flow.
 * - Fields that are genuinely identical across combos on the same platform (e.g.
 *   Landing Page URL, Brand Name, Language for Google/Meta) are normalized into a
 *   single shared input, applied to every selected combo of that platform.
 * - Fields specific to one combo (e.g. Google Search's ASINs, Standard Shopping's
 *   Product IDs, Meta's audience targeting) are namespaced per-combo so multiple
 *   Google combos can be selected at once without colliding.
 */

import { PLATFORM_BUDGET_TYPES, META_LOCATION_TYPE_OPTIONS } from './constants';

export { META_LOCATION_TYPE_OPTIONS };

export const MPBT_PLATFORM_LABELS = {
  amazon: 'Amazon',
  google: 'Google',
  meta: 'Meta',
};

// The fixed set of Platform + Budget Type combinations MPBT supports. Each entry is
// an independently-selectable checkbox with its own daily budget. Extend this list
// (and MpcPlatformFieldsSection.jsx) to add more combos later.
export const MPBT_BUDGET_COMBOS = [
  {
    key: 'amazon_item', platform: 'amazon', budgetTypeValue: 'item', label: 'Amazon - Item', nameSuffix: 'Amazon Item',
  },
  {
    key: 'google_pmax', platform: 'google', budgetTypeValue: 'google_pmax', label: 'Google - Performance Max', nameSuffix: 'Google Performance Max',
  },
  {
    key: 'google_search', platform: 'google', budgetTypeValue: 'google_search', label: 'Google - Search', nameSuffix: 'Google Search',
  },
  {
    key: 'google_shop', platform: 'google', budgetTypeValue: 'google_shop', label: 'Google - Standard Shopping', nameSuffix: 'Google Standard Shopping',
  },
  {
    key: 'meta_facebook_instagram', platform: 'meta', budgetTypeValue: 'meta_facebook_instagram', label: 'Meta - Facebook Instagram', nameSuffix: 'Meta Facebook Instagram',
  },
];

/**
 * Look up a combo's real PLATFORM_BUDGET_TYPES option (adType, itemType, fields),
 * i.e. the same metadata the single-platform GroupEditModal form uses to decide
 * which extra fields (URL, ASINs, Product IDs, etc.) to show.
 * @param {Object} combo - An entry from MPBT_BUDGET_COMBOS
 * @returns {Object|null} The matching PLATFORM_BUDGET_TYPES[platform].options entry
 */
export function getComboBudgetTypeOption(combo) {
  const platformConfig = PLATFORM_BUDGET_TYPES[combo.platform];
  if (!platformConfig) return null;
  return platformConfig.options.find(opt => opt.value === combo.budgetTypeValue) || null;
}

/**
 * Find an MPBT_BUDGET_COMBOS entry by its key.
 * @param {string} key - Combo key (e.g. 'google_search')
 * @returns {Object|null}
 */
export function getComboByKey(key) {
  return MPBT_BUDGET_COMBOS.find(combo => combo.key === key) || null;
}

/**
 * Reverse-lookup: given a platform and its own saved budget-type value (as derived
 * by deriveRowBudgetTypeValue below), find the matching MPBT combo. Used by the list
 * view to figure out which combos already exist for a given account/prefix.
 * @param {string} platform - Platform key
 * @param {string} platformBudgetTypeValue - The platform's own budget type value
 * @returns {Object|null} The matching MPBT_BUDGET_COMBOS entry, or null
 */
export function resolveComboForRow(platform, platformBudgetTypeValue) {
  if (!platform || !platformBudgetTypeValue) return null;
  return MPBT_BUDGET_COMBOS.find(combo => (
    combo.platform === platform && combo.budgetTypeValue === platformBudgetTypeValue
  )) || null;
}

/**
 * Derive a row's own platform-specific budget type value the same way
 * GroupEditModal.jsx's getInitialValues() does (from ad_type for Google/Meta, or
 * budget_type directly for Amazon/Walmart/etc.), so callers can feed it into
 * resolveComboForRow().
 * @param {string} platform - Platform key
 * @param {Object} row - A BGD row as returned by getBudgetGroups()
 * @returns {string|null} Platform-specific budget type value
 */
export function deriveRowBudgetTypeValue(platform, row) {
  if (!row) return null;

  if (platform === 'google') {
    const adTypeMap = {
      SEARCH: 'google_search',
      'PERFORMANCE MAX': 'google_pmax',
      'STANDARD SHOPPING': 'google_shop',
    };
    if (row.ad_type && adTypeMap[row.ad_type]) {
      return adTypeMap[row.ad_type];
    }
    return row.budget_type || null;
  }

  if (platform === 'meta') {
    const adTypeMap = {
      'FACEBOOK + INSTAGRAM FEED': 'meta_facebook_instagram',
      'FACEBOOK FEED': 'meta_facebook',
      'INSTAGRAM FEED': 'meta_instagram',
      'META AUDIENCE NETWORK': 'meta_audience_network',
    };
    if (row.ad_type && adTypeMap[row.ad_type]) {
      return adTypeMap[row.ad_type];
    }
    return row.budget_type || null;
  }

  // Amazon/Walmart/etc.: budget_type is used directly ('item', 'brand', 'dsp', 'other')
  return row.budget_type ? row.budget_type.toLowerCase() : null;
}

/**
 * Strip the trailing " - {ComboNameSuffix}" suffix that MPBT appends to budget group
 * names on save, returning the shared name prefix. If the name doesn't end with a
 * known combo suffix, the name is returned unchanged.
 * @param {string} budgetGroupName - Full budget_group_name (e.g. "BG Cavu 30d - Google Search")
 * @returns {string} The stripped prefix (e.g. "BG Cavu 30d")
 */
export function stripComboSuffix(budgetGroupName) {
  if (!budgetGroupName) return '';
  const matchingCombo = MPBT_BUDGET_COMBOS.find(combo => budgetGroupName.endsWith(` - ${combo.nameSuffix}`));
  if (matchingCombo) {
    return budgetGroupName.slice(0, budgetGroupName.length - ` - ${matchingCombo.nameSuffix}`.length);
  }
  return budgetGroupName;
}

// ==============================================================================
// LANGUAGE NORMALIZATION (per-platform Google, Meta)
// ==============================================================================

// Normalized Language options for MPBT. Each entry represents one human language and
// carries the DIFFERENT underlying values each platform actually needs:
//   - metaValue: Meta's locale code (e.g. "en_US"), stored directly in budget_json
//   - googleSearchTerm: a plain-English search term used to resolve Google's actual
//     languageConstants/NNNN resource name via the existing live
//     searchGoogleLanguageConstants() API at submit time (Google's language
//     constant IDs aren't safe to hardcode/guess, so we look them up for real).
export const MPBT_LANGUAGE_OPTIONS = [
  { label: 'English (US)', value: 'en_US', metaValue: 'en_US', googleSearchTerm: 'English' },
  { label: 'English (UK)', value: 'en_GB', metaValue: 'en_GB', googleSearchTerm: 'English' },
  { label: 'Spanish (Spain)', value: 'es_ES', metaValue: 'es_ES', googleSearchTerm: 'Spanish' },
  { label: 'Spanish (Mexico)', value: 'es_MX', metaValue: 'es_MX', googleSearchTerm: 'Spanish' },
  { label: 'French (France)', value: 'fr_FR', metaValue: 'fr_FR', googleSearchTerm: 'French' },
  { label: 'German (Germany)', value: 'de_DE', metaValue: 'de_DE', googleSearchTerm: 'German' },
  { label: 'Italian (Italy)', value: 'it_IT', metaValue: 'it_IT', googleSearchTerm: 'Italian' },
  { label: 'Portuguese (Brazil)', value: 'pt_BR', metaValue: 'pt_BR', googleSearchTerm: 'Portuguese' },
  { label: 'Japanese (Japan)', value: 'ja_JP', metaValue: 'ja_JP', googleSearchTerm: 'Japanese' },
  { label: 'Chinese (Simplified)', value: 'zh_CN', metaValue: 'zh_CN', googleSearchTerm: 'Chinese (simplified)' },
];

/**
 * Resolve the Google Ads languageConstants/NNNN resource names for the given
 * normalized MPBT language values by fuzzy-searching the live Google language
 * constants API (searchGoogleLanguageConstants), matching results by name.
 * Falls back to skipping any language that can't be resolved (rather than
 * guessing an ID), so a bad match never silently applies the wrong language.
 * @param {Array<string>} languageValues - MPBT_LANGUAGE_OPTIONS values (e.g. ['en_US'])
 * @param {Function} searchFn - budgetApi.js searchGoogleLanguageConstants function
 * @param {string} jwtToken - JWT token
 * @returns {Promise<Array<string>>} Array of Google languageConstants/NNNN resource names
 */
export async function resolveGoogleLanguageConstants(languageValues, searchFn, jwtToken) {
  if (!languageValues || languageValues.length === 0) {
    return [];
  }

  const searchTerms = [...new Set(
    languageValues
      .map(value => MPBT_LANGUAGE_OPTIONS.find(opt => opt.value === value)?.googleSearchTerm)
      .filter(Boolean),
  )];

  const resolved = await Promise.all(searchTerms.map(async (searchTerm) => {
    const results = await searchFn({ searchTerm, jwtToken });
    // Prefer an exact (case-insensitive) name match over the first fuzzy result
    const exactMatch = results.find(option => option.label?.toLowerCase() === searchTerm.toLowerCase());
    return (exactMatch || results[0])?.value;
  }));

  return resolved.filter(Boolean);
}
